import React, { useEffect, useState, useRef, createElement } from 'react';
declare global {
  interface Window {
    _wq: any[];
  }
}
interface VideoPlayerProps {
  mediaId?: string;
  onEnded?: () => void;
  autoPlay?: boolean;
}
export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  mediaId = 'vic2dhkti8',
  onEnded,
  autoPlay = false
}) => {
  const playerScriptRef = useRef<HTMLScriptElement | null>(null);
  const embedScriptRef = useRef<HTMLScriptElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    const loadScripts = async () => {
      try {
        // Load player script
        if (!playerScriptRef.current) {
          const playerScript = document.createElement('script');
          playerScript.src = 'https://fast.wistia.com/player.js';
          playerScript.async = true;
          playerScript.crossOrigin = 'anonymous';
          playerScript.onerror = () => {
            console.error('Failed to load Wistia player script');
            setHasError(true);
          };
          playerScriptRef.current = playerScript;
          document.head.appendChild(playerScript);
          // Wait for player script to load
          await new Promise((resolve, reject) => {
            playerScript.onload = resolve;
            playerScript.onerror = reject;
          });
        }
        // Load embed script
        if (!embedScriptRef.current) {
          const embedScript = document.createElement('script');
          embedScript.src = `https://fast.wistia.com/embed/${mediaId}.js`;
          embedScript.async = true;
          embedScript.crossOrigin = 'anonymous';
          embedScript.type = 'module';
          embedScript.onerror = () => {
            console.error('Failed to load Wistia embed script');
            setHasError(true);
          };
          embedScriptRef.current = embedScript;
          document.head.appendChild(embedScript);
          // Wait for embed script to load
          await new Promise((resolve, reject) => {
            embedScript.onload = resolve;
            embedScript.onerror = reject;
          });
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading Wistia scripts:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };
    loadScripts();
    // Setup Wistia API callbacks
    window._wq = window._wq || [];
    window._wq.push({
      id: mediaId,
      options: {
        playbackRate: 1.25,
        plugin: {
          captions: {
            onByDefault: true,
            language: 'eng'
          }
        }
      },
      onReady: (video: any) => {
        // Force playback rate
        video.playbackRate(1.25);
        // Enforce playback rate and captions when video starts playing
        video.bind('play', () => {
          if (video.playbackRate() !== 1.25) {
            video.playbackRate(1.25);
          }
          if (video.plugin && video.plugin('captions')) {
            video.plugin('captions').turnOn('eng');
            video.plugin('captions').turnOn('en'); // Try both common language codes
          }
        });
        // Force captions on immediately as well
        setTimeout(() => {
          if (video.plugin && video.plugin('captions')) {
            video.plugin('captions').turnOn('eng');
            video.plugin('captions').turnOn('en');
          }
        }, 500);
        if (autoPlay) {
          video.play();
        }
        // Bind ended event
        video.bind('end', () => {
          if (onEnded) onEnded();
        });
      }
    });
    return () => {
      if (playerScriptRef.current && document.head.contains(playerScriptRef.current)) {
        document.head.removeChild(playerScriptRef.current);
      }
      if (embedScriptRef.current && document.head.contains(embedScriptRef.current)) {
        document.head.removeChild(embedScriptRef.current);
      }
    };
  }, [mediaId]);
  if (hasError) {
    return <div className="relative w-full rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center" data-id="element-153">
        <p className="text-white text-center px-4" data-id="element-154">
          Unable to load video player. Please refresh the page or try again
          later.
        </p>
      </div>;
  }
  return <div className="relative w-full rounded-lg overflow-hidden bg-black aspect-video group" data-id="element-155">
      <style data-id="element-156">
        {`
          wistia-player[media-id='${mediaId}']:not(:defined) {
            background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${mediaId}/swatch');
            display: block;
            filter: blur(5px);
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
          wistia-player[media-id='${mediaId}'] {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
        `}
      </style>
      <wistia-player media-id={mediaId} aspect="1.7777777777777777" style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    }} data-id="element-157" />
      {isLoading && <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center" data-id="element-158">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-4 border-white border-t-transparent" data-id="element-159" />
        </div>}
    </div>;
};